import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AppShell } from "@/components/AppShell";
import { GeschaeftspartnerLogo } from "@/components/GeschaeftspartnerLogo";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { SegmentedControl } from "@/components/ui/SegmentedControl";
import { Spinner } from "@/components/ui/Spinner";
import { ApiError } from "@/lib/apiClient";
import {
  geschaeftspartnerDetailPath,
  syncKontakteFromHero,
  type Geschaeftspartner,
  type GeschaeftspartnerTyp,
  type ListGeschaeftspartnerParams,
} from "@/lib/kontakte";
import {
  loadGeschaeftspartnerList,
  readGeschaeftspartnerListCache,
} from "@/pages/kontakte/geschaeftspartnerListCache";
import { useRealtimeSync } from "@/lib/useRealtime";
import { StandardZahlungszieleCard } from "@/pages/kontakte/StandardZahlungszieleCard";
import { GeschaeftspartnerColumnsDialog } from "@/pages/kontakte/GeschaeftspartnerColumnsDialog";
import { GeschaeftspartnerCreateDialog } from "@/pages/kontakte/GeschaeftspartnerCreateDialog";
import { PrivatpersonBadge } from "@/pages/kontakte/PartnerNameFields";
import { GeschaeftspartnerTable } from "@/pages/kontakte/GeschaeftspartnerTable";
import {
  clampGeschaeftspartnerColumnWidth,
  formatGeschaeftspartnerAktivitaet,
  formatOrt,
} from "@/pages/kontakte/geschaeftspartnerColumns";
import {
  getVisibleGeschaeftspartnerColumns,
  getVisibleGeschaeftspartnerWidths,
  useGeschaeftspartnerTableSettings,
} from "@/pages/kontakte/useGeschaeftspartnerTableSettings";
import {
  readGeschaeftspartnerListMode,
  readGeschaeftspartnerListView,
  writeGeschaeftspartnerListMode,
  writeGeschaeftspartnerListView,
  type GeschaeftspartnerListMode,
} from "@/pages/kontakte/geschaeftspartnerListView";
import { SearchInput } from "@/pages/projekte/SearchInput";
import { useTableSort, type SortState } from "@/components/dataTable";

const PAGE_SIZE = 100;

/** Server-Sortierung für „Top zuerst“ (siehe api/kontakte.py `aktivitaet`). */
const TOP_SORT: SortState = { key: "aktivitaet", direction: "desc" };

interface GeschaeftspartnerListPageProps {
  typ: GeschaeftspartnerTyp;
  title: string;
  detailBasePath: string;
}

function listParams(
  search: string,
  offset: number,
  sort: SortState | null,
): ListGeschaeftspartnerParams {
  return {
    q: search.trim() || undefined,
    limit: PAGE_SIZE,
    offset,
    sort: sort?.key,
    direction: sort?.direction,
  };
}

function openingCache(typ: GeschaeftspartnerTyp) {
  const view = readGeschaeftspartnerListView(typ);
  const mode = readGeschaeftspartnerListMode(typ);
  return readGeschaeftspartnerListCache(
    typ,
    listParams(view.q, view.offset, mode === "top" ? TOP_SORT : null),
  );
}

function formatCount(typ: GeschaeftspartnerTyp, total: number): string {
  const label = typ === "kunde" ? "Kunde" : "Lieferant";
  const plural = typ === "kunde" ? "Kunden" : "Lieferanten";
  if (total === 1) return `1 ${label}`;
  return `${total.toLocaleString("de-DE")} ${plural}`;
}

export function GeschaeftspartnerListPage({
  typ,
  title,
  detailBasePath,
}: GeschaeftspartnerListPageProps) {
  const navigate = useNavigate();
  const { sort, toggle: toggleSort, reset: resetSort } = useTableSort();
  const [mode, setMode] = useState<GeschaeftspartnerListMode>(() =>
    readGeschaeftspartnerListMode(typ),
  );
  const [rows, setRows] = useState<Geschaeftspartner[]>(() => openingCache(typ)?.items ?? []);
  const [total, setTotal] = useState(() => openingCache(typ)?.total ?? 0);
  const [search, setSearch] = useState(() => readGeschaeftspartnerListView(typ).q);
  const [offset, setOffset] = useState(() => readGeschaeftspartnerListView(typ).offset);
  const preservedSortRef = useRef(sort);

  // Spaltensortierung hat Vorrang; ohne sie gilt der gewählte Modus.
  const effectiveSort: SortState | null = sort ?? (mode === "top" ? TOP_SORT : null);
  const showRanking = mode === "top" && sort == null;

  const handleModeChange = useCallback(
    (next: GeschaeftspartnerListMode) => {
      setMode(next);
      writeGeschaeftspartnerListMode(typ, next);
      resetSort();
      setOffset(0);
    },
    [typ, resetSort],
  );
  const [loading, setLoading] = useState(() => openingCache(typ) == null);
  const [error, setError] = useState<string | null>(null);
  const [columnsDialogOpen, setColumnsDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState<string | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);
  const { settings, saveSettings } = useGeschaeftspartnerTableSettings(typ);
  const requestSeqRef = useRef(0);

  const visibleColumns = useMemo(
    () => getVisibleGeschaeftspartnerColumns(settings, typ),
    [settings, typ],
  );

  const visibleWidths = useMemo(() => getVisibleGeschaeftspartnerWidths(settings), [settings]);

  const handleColumnWidthChange = useCallback(
    (key: string, width: number) => {
      saveSettings({
        columns: settings.columns,
        widths: {
          ...(settings.widths ?? {}),
          [key]: clampGeschaeftspartnerColumnWidth(width),
        },
      });
    },
    [settings, saveSettings],
  );

  const reload = useCallback(async (options?: { silent?: boolean }) => {
    const seq = ++requestSeqRef.current;
    const params = listParams(search, offset, effectiveSort);
    const cached = readGeschaeftspartnerListCache(typ, params);
    if (cached) {
      setRows(cached.items);
      setTotal(cached.total);
      setLoading(false);
    } else if (!options?.silent) {
      setRows([]);
      setTotal(0);
      setLoading(true);
    }
    setError(null);
    try {
      const data = await loadGeschaeftspartnerList(typ, params);
      if (seq !== requestSeqRef.current) return;
      setRows(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      if (seq !== requestSeqRef.current) return;
      setError(
        err instanceof ApiError
          ? err.message
          : `${title} konnten nicht geladen werden.`,
      );
      if (!options?.silent && !cached) {
        setRows([]);
        setTotal(0);
      }
    } finally {
      if (seq === requestSeqRef.current) setLoading(false);
    }
  }, [typ, title, search, offset, effectiveSort?.key, effectiveSort?.direction]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // Die andere Liste erst holen, wenn diese Seite fertig ist – nicht parallel zur ersten Abfrage.
  const prefetchedTypRef = useRef<GeschaeftspartnerTyp | null>(null);
  useEffect(() => {
    if (loading || prefetchedTypRef.current === typ) return;
    prefetchedTypRef.current = typ;
    const other: GeschaeftspartnerTyp = typ === "kunde" ? "lieferant" : "kunde";
    const view = readGeschaeftspartnerListView(other);
    const mode = readGeschaeftspartnerListMode(other);
    void loadGeschaeftspartnerList(
      other,
      listParams(view.q, view.offset, mode === "top" ? TOP_SORT : null),
    ).catch(() => {
      // Fehler zeigt die andere Seite selbst, wenn sie geöffnet wird.
    });
  }, [typ, loading]);

  const handleSearchChange = useCallback((next: string) => {
    setSearch(next);
    setOffset(0);
  }, []);

  useEffect(() => {
    // Beim Öffnen der Seite bleibt die gespeicherte Seite stehen. Erst eine
    // neue Sortierung setzt sie zurück; die Suche selbst setzt sie direkt.
    if (sort === preservedSortRef.current) return;
    preservedSortRef.current = sort;
    setOffset(0);
  }, [sort]);

  useEffect(() => {
    writeGeschaeftspartnerListView(typ, { q: search, offset });
  }, [typ, search, offset]);

  // Realtime-Änderungen still einpflegen – die Tabelle bleibt dabei stehen.
  useRealtimeSync({
    domains: ["kontakte"],
    onChange: () => void reload({ silent: true }),
  });

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

  const openDetail = useCallback(
    (row: Geschaeftspartner) => {
      navigate(geschaeftspartnerDetailPath(detailBasePath, row));
    },
    [detailBasePath, navigate],
  );

  const handleHeroSync = useCallback(async () => {
    setSyncing(true);
    setSyncMessage(null);
    setSyncError(null);
    try {
      const result = await syncKontakteFromHero();
      const mitNummer = result.lieferanten_mit_nummer;
      const nummerHinweis =
        typeof mitNummer === "number"
          ? mitNummer > 0
            ? ` Davon haben ${mitNummer.toLocaleString("de-DE")} eine Lieferantennummer aus Hero.`
            : " Hero hat für keinen Lieferanten eine Nummer geliefert – bitte prüfen, ob die Nummern in Hero gepflegt sind."
          : "";
      setSyncMessage(
        `Hero-Sync abgeschlossen: ${result.lieferanten.toLocaleString("de-DE")} Lieferanten und ${result.kunden.toLocaleString("de-DE")} Kunden synchronisiert.${nummerHinweis}`,
      );
      await reload({ silent: true });
    } catch (err) {
      setSyncError(
        err instanceof ApiError
          ? err.message
          : "Hero-Sync fehlgeschlagen. Bitte später erneut versuchen.",
      );
    } finally {
      setSyncing(false);
    }
  }, [reload]);

  return (
    <AppShell
      title={title}
      subtitle={loading && rows.length === 0 ? undefined : formatCount(typ, total)}
      actions={
        <div className="flex flex-wrap gap-2">
          {typ === "lieferant" ? (
            <Button
              variant="secondary"
              disabled={syncing}
              onClick={() => void handleHeroSync()}
            >
              {syncing ? "Sync läuft…" : "Hero-Sync"}
            </Button>
          ) : null}
          <Button onClick={() => setCreateDialogOpen(true)}>
            {typ === "kunde" ? "Kunde anlegen" : "Lieferant anlegen"}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {error ? (
          <Alert variant="error" title="Fehler">
            {error}
          </Alert>
        ) : null}

        {syncError ? (
          <Alert variant="error" title="Hero-Sync fehlgeschlagen">
            {syncError}
          </Alert>
        ) : null}

        {syncMessage ? (
          <Alert variant="success" title="Hero-Sync">
            {syncMessage}
          </Alert>
        ) : null}

        {typ === "kunde" ? <StandardZahlungszieleCard /> : null}

        <section className="rounded-ios-lg bg-card px-4 py-3 shadow-ios-md ring-1 ring-separator/35 dark:ring-separator/50 md:px-5">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <SearchInput
              value={search}
              onChange={handleSearchChange}
              placeholder={`${title} oder Ansprechpartner suchen…`}
              aria-label={`${title} oder Ansprechpartner suchen`}
              className="min-w-0 flex-1 md:max-w-xl"
            />
            <div className="flex flex-wrap items-center gap-2">
              <SegmentedControl
                name="Reihenfolge"
                size="md"
                value={mode}
                onChange={handleModeChange}
                options={[
                  { value: "alpha", label: "A–Z" },
                  { value: "top", label: typ === "kunde" ? "Top-Kunden" : "Top-Lieferanten" },
                ]}
              />
              <Button
                type="button"
                variant="secondary"
                className="hidden md:inline-flex"
                onClick={() => setColumnsDialogOpen(true)}
              >
                Spalten
              </Button>
            </div>
          </div>
          {mode === "top" ? (
            <p className="mt-2 text-xs text-label-tertiary">
              {typ === "kunde"
                ? "Sortiert nach Anzahl der Projekte – die aktivsten Kunden stehen oben."
                : "Sortiert nach Bestellungen und erkannten Eingangsrechnungen – die aktivsten Lieferanten stehen oben."}
              {sort ? " Eine Spaltensortierung ist aktiv und überlagert die Reihenfolge." : ""}
            </p>
          ) : null}
        </section>

        {loading && rows.length === 0 ? (
          <div className="flex justify-center py-12">
            <Spinner />
          </div>
        ) : rows.length === 0 ? (
          <Alert variant="info" title="Keine Einträge">
            {search.trim()
              ? "Keine Treffer für die Suche."
              : `Noch keine ${title.toLowerCase()} vorhanden. Legen Sie einen neuen Eintrag an oder starten Sie einen Hero-Sync.`}
          </Alert>
        ) : (
          <>
            <ul className="space-y-2 md:hidden" role="list">
              {rows.map((row) => (
                <li key={row.id}>
                  <Link
                    to={geschaeftspartnerDetailPath(detailBasePath, row)}
                    className="flex w-full items-start gap-3 rounded-ios-lg border border-separator/80 bg-card px-4 py-3 text-left shadow-ios transition hover:border-accent/40 hover:bg-fill-tertiary/50 active:bg-fill-secondary/40"
                  >
                    <GeschaeftspartnerLogo
                      partnerId={row.id}
                      name={row.firmenname}
                      hasLogo={row.has_logo}
                      logoUpdatedAt={row.logo_updated_at}
                      className="mt-0.5 h-10 w-10 rounded-xl text-xs"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <span className="flex min-w-0 items-center gap-2 font-semibold text-label">
                          <span className="truncate">{row.firmenname}</span>
                          {row.partnerart === "privatperson" ? <PrivatpersonBadge /> : null}
                        </span>
                        {typ === "kunde" && row.kundennummer?.trim() ? (
                          <span className="shrink-0 rounded-full bg-fill-tertiary px-2 py-0.5 tabular-nums text-xs text-label-secondary">
                            {row.kundennummer.trim()}
                          </span>
                        ) : null}
                        {typ === "lieferant" && row.lieferantennummer?.trim() ? (
                          <span className="shrink-0 rounded-full bg-fill-tertiary px-2 py-0.5 tabular-nums text-xs text-label-secondary">
                            {row.lieferantennummer.trim()}
                          </span>
                        ) : null}
                      </div>
                      {row.email ? (
                        <span className="mt-0.5 block truncate text-sm text-label-secondary">
                          {row.email}
                        </span>
                      ) : null}
                      <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-label-tertiary">
                        {showRanking ? (
                          <span className="font-medium text-label-secondary">
                            {formatGeschaeftspartnerAktivitaet(row, typ)}
                          </span>
                        ) : null}
                        {row.telefon ? <span>{row.telefon}</span> : null}
                        {formatOrt(row) !== "—" ? <span>{formatOrt(row)}</span> : null}
                        {row.matched_ansprechpartner ? (
                          <span className="font-medium text-label-secondary">
                            Ansprechpartner: {row.matched_ansprechpartner}
                          </span>
                        ) : null}
                        <span>{row.ansprechpartner_count ?? 0} Ansprechpartner</span>
                      </div>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>

            <div className="hidden md:block">
              <GeschaeftspartnerTable
                rows={rows}
                columns={visibleColumns}
                widths={visibleWidths}
                onColumnWidthChange={handleColumnWidthChange}
                onRowClick={openDetail}
                sort={sort}
                onSort={toggleSort}
                ranking={showRanking ? { typ } : null}
              />
            </div>

            {pageCount > 1 ? (
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm text-label-secondary">
                  Seite {currentPage} von {pageCount}
                </span>
                <div className="flex gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={offset === 0}
                    onClick={() => setOffset((v) => Math.max(0, v - PAGE_SIZE))}
                  >
                    Zurück
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={offset + PAGE_SIZE >= total}
                    onClick={() => setOffset((v) => v + PAGE_SIZE)}
                  >
                    Weiter
                  </Button>
                </div>
              </div>
            ) : null}
          </>
        )}
      </div>

      <GeschaeftspartnerCreateDialog
        open={createDialogOpen}
        typ={typ}
        onClose={() => setCreateDialogOpen(false)}
        onCreated={(gp) => {
          navigate(`${detailBasePath}/${gp.id}`);
        }}
      />

      <GeschaeftspartnerColumnsDialog
        open={columnsDialogOpen}
        onClose={() => setColumnsDialogOpen(false)}
        typ={typ}
        settings={settings}
        onSave={saveSettings}
      />
    </AppShell>
  );
}
